import {useState} from 'react';

import {addToast} from '@albert-io/atomic';

import {createLtiLineItemQuery, deleteLtiLineItemQuery} from './LtiCell.queries';

export interface UseLtiSyncResult {
  hasSyncedWithLti: boolean;
  resyncCompleted: boolean;
  setResyncCompleted: (value: boolean) => void;
  isRequestPending: boolean;
  /** Last sync/re-sync attempt failed; cleared when a new attempt starts or one succeeds. */
  syncError: boolean;
  syncLti: () => Promise<boolean>;
  resyncLti: () => Promise<void>;
}

/**
 * Per-assignment LMS (LTI) grade-sync state + actions, backed by the Mandark `lti/line_item_v1`
 * POST/DELETE endpoints. Extracted verbatim from LtiCell so the v2 dashboard assignment row can
 * reuse the exact sync behavior instead of reimplementing it. `hasLtiLineItemId` seeds the initial
 * synced state (presence of a classroom-scoped line item = already synced).
 */
export const useLtiSync = (
  assignmentId: string,
  classroomId: string,
  schoolId: string | null,
  hasLtiLineItemId: boolean
): UseLtiSyncResult => {
  const [hasSyncedWithLti, setHasSyncedWithLti] = useState(hasLtiLineItemId);
  const [resyncCompleted, setResyncCompleted] = useState(false);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [syncError, setSyncError] = useState(false);

  const handleLtiSyncError = (e: any) => {
    // Network-level failures have no response body, and the errors array can be empty
    const error = e?.response?.body?.errors?.[0];
    if (error?.code === 'Errors.ThirdPartyHTTPRequestError') {
      addToast({
        color: 'negative',
        title: 'Incorrect Permissions',
        message:
          "Your school's technical admin needs to adjust Albert's permissions on your LMS to include grades and assignments."
      });
    } else {
      addToast({
        color: 'negative',
        message: 'There was an error syncing your classroom data.'
      });
    }
  };

  const createLtiLineItem = async () => {
    try {
      await createLtiLineItemQuery(assignmentId, classroomId, schoolId as string);
      return true;
    } catch (e) {
      handleLtiSyncError(e);
      return false;
    }
  };

  const deleteLtiLineItem = async () => {
    try {
      await deleteLtiLineItemQuery(assignmentId, classroomId, schoolId as string);
      return true;
    } catch (e) {
      handleLtiSyncError(e);
      return false;
    }
  };

  const syncLti = async () => {
    setIsRequestPending(true);
    setSyncError(false);
    const syncSuccess = await createLtiLineItem();
    setHasSyncedWithLti(syncSuccess);
    setSyncError(!syncSuccess);
    setIsRequestPending(false);
    return syncSuccess;
  };

  const resyncLti = async () => {
    setIsRequestPending(true);
    setSyncError(false);
    const deleteSuccess = await deleteLtiLineItem();
    const syncSuccess = deleteSuccess ? await createLtiLineItem() : false;
    setHasSyncedWithLti(syncSuccess);
    setResyncCompleted(syncSuccess);
    setSyncError(!syncSuccess);
    setIsRequestPending(false);
  };

  return {
    hasSyncedWithLti,
    resyncCompleted,
    setResyncCompleted,
    isRequestPending,
    syncError,
    syncLti,
    resyncLti
  };
};
